import { COORDINATE_SPACE } from "@perceptron/utils/math";
import type { BoxCoordinates } from "../utils/coordinates";
import type { IntervalFrames, Rect } from "./contracts";

export const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

export function validBox(box: Rect | null | undefined): box is Rect {
  return Boolean(box && Object.values(box).every(Number.isFinite) && box.width > 0 && box.height > 0);
}

/** Blend measured boxes by center and log size; no trajectory is inferred from GT anchors. */
export function blendBoxes(a: Rect, b: Rect, t: number): Rect {
  if (!validBox(a) || !validBox(b) || !Number.isFinite(t)) throw new Error("Invalid tracking box blend");
  if (t <= 0) return { ...a };
  if (t >= 1) return { ...b };
  const width = Math.exp(Math.log(a.width) * (1 - t) + Math.log(b.width) * t);
  const height = Math.exp(Math.log(a.height) * (1 - t) + Math.log(b.height) * t);
  const cx = (a.x + a.width / 2) * (1 - t) + (b.x + b.width / 2) * t;
  const cy = (a.y + a.height / 2) * (1 - t) + (b.y + b.height / 2) * t;
  return { x: cx - width / 2, y: cy - height / 2, width, height };
}

export function boxIoU(a: Rect, b: Rect): number {
  if (!validBox(a) || !validBox(b)) return 0;
  const width = Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x));
  const height = Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));
  const intersection = width * height;
  return intersection / (a.width * a.height + b.width * b.height - intersection);
}

type FrameGeometry = Pick<IntervalFrames, "sourceWidth" | "sourceHeight" | "roi" | "scaleX" | "scaleY">;

/** Full source-video bounds expressed in resized ROI pixels, including areas outside the crop. */
export function sourceFrameBounds({ sourceWidth, sourceHeight, roi, scaleX, scaleY }: FrameGeometry): Rect {
  return {
    x: -roi.x * scaleX,
    y: -roi.y * scaleY,
    width: sourceWidth * scaleX,
    height: sourceHeight * scaleY,
  };
}

/** Keep the visible intersection; an offscreen or invalid prediction has no displayable box. */
export function clipRectToBounds(box: Rect, bounds: Rect): Rect | null {
  if (!validBox(box) || !validBox(bounds)) return null;
  const x = Math.max(box.x, bounds.x);
  const y = Math.max(box.y, bounds.y);
  const right = Math.min(box.x + box.width, bounds.x + bounds.width);
  const bottom = Math.min(box.y + box.height, bounds.y + bounds.height);
  if (right <= x || bottom <= y) return null;
  // Preserve exact GT coordinates and uncut dimensions, avoiding rounding from subtracting edges.
  return {
    x,
    y,
    width: x === box.x && right === box.x + box.width ? box.width : right - x,
    height: y === box.y && bottom === box.y + box.height ? box.height : bottom - y,
  };
}

/** Convert an original 0–1000 annotation into resized ROI pixels. */
export function boxToRoiRect(box: BoxCoordinates, geometry: FrameGeometry): Rect {
  const { sourceWidth, sourceHeight, roi, scaleX, scaleY } = geometry;
  return {
    x: ((box.top_left.x / COORDINATE_SPACE) * sourceWidth - roi.x) * scaleX,
    y: ((box.top_left.y / COORDINATE_SPACE) * sourceHeight - roi.y) * scaleY,
    width: ((box.bottom_right.x - box.top_left.x) / COORDINATE_SPACE) * sourceWidth * scaleX,
    height: ((box.bottom_right.y - box.top_left.y) / COORDINATE_SPACE) * sourceHeight * scaleY,
  };
}

/** Convert resized ROI pixels back to the original annotation coordinate space. */
export function roiRectToBox(rect: Rect, geometry: FrameGeometry): BoxCoordinates {
  const { sourceWidth, sourceHeight, roi, scaleX, scaleY } = geometry;
  const coordinate = (value: number, scale: number, offset: number, size: number) => {
    const normalized = ((value / scale + offset) / size) * COORDINATE_SPACE;
    // Round-trip arithmetic at a clipped edge must not fail strict recovery bounds validation.
    const tolerance = Number.EPSILON * COORDINATE_SPACE * 8;
    if (normalized < 0 && normalized >= -tolerance) return 0;
    if (normalized > COORDINATE_SPACE && normalized <= COORDINATE_SPACE + tolerance) return COORDINATE_SPACE;
    return normalized;
  };
  return {
    top_left: {
      x: coordinate(rect.x, scaleX, roi.x, sourceWidth),
      y: coordinate(rect.y, scaleY, roi.y, sourceHeight),
    },
    bottom_right: {
      x: coordinate(rect.x + rect.width, scaleX, roi.x, sourceWidth),
      y: coordinate(rect.y + rect.height, scaleY, roi.y, sourceHeight),
    },
  };
}
